import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const routePermissions = {
  '/': ['tenant_admin', 'manager', 'attendant'],
  '/master-admin/dashboard': ['master_admin'],
  '/master-admin/tenants': ['master_admin'],
  '/master-admin/reports': ['master_admin'],
  '/master-admin/users': ['master_admin'],
  '/master-admin/audit-logs': ['master_admin'],
  '/master-admin/settings': ['master_admin'],
  '/conversations': ['tenant_admin', 'manager', 'attendant'],
  '/conversation': ['tenant_admin', 'manager', 'attendant'],
  '/my-conversations': ['attendant'],
  '/contacts': ['tenant_admin', 'manager', 'attendant'],
  '/tasks': ['tenant_admin', 'manager', 'attendant'],
  '/professional-config': ['tenant_admin', 'manager'],
  '/leads': ['tenant_admin', 'manager'],
  '/team-performance': ['tenant_admin', 'manager'],
  '/appointments': ['tenant_admin', 'manager', 'attendant'],
  '/bi-integration': ['tenant_admin', 'manager'],
  '/envios': ['tenant_admin', 'manager', 'attendant'],
  '/achievements': ['tenant_admin', 'manager', 'attendant'],
  '/attendee-dashboard': ['attendant'],
  '/users': ['tenant_admin', 'manager'],
  '/sectors': ['tenant_admin', 'manager'],
  '/phone-numbers': ['tenant_admin', 'manager'],
  '/templates': ['tenant_admin', 'manager'],
  '/bulk-messaging': ['tenant_admin', 'manager'],
  '/automations': ['tenant_admin', 'manager'],
  '/ai-agents': ['tenant_admin', 'manager'],
  '/opt-in': ['tenant_admin', 'manager'],
  '/chatbots': ['tenant_admin', 'manager'],
  '/template-variables': ['tenant_admin', 'manager'],
  '/reports': ['tenant_admin', 'manager'],
  '/business-accounts': ['tenant_admin', 'manager'],
  '/embedded-signup': ['tenant_admin', 'manager'],
  '/profile': ['master_admin', 'tenant_admin', 'manager', 'attendant'],
};

const defaultRoutesByRole = {
  master_admin: '/master-admin/dashboard',
  tenant_admin: '/',
  manager: '/',
  attendant: '/my-conversations',
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [tenantPlan, setTenantPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('NO_TOKEN');
        setLoading(false);
        return;
      }

      try {
        const res = await api.get('/api/profile/me');
        // ⭐ CORREÇÃO: Garantir que userData exista antes de acessar propriedades
        const userData = res.data?.user || res.data;
        
        if (!userData) {
          throw new Error('Dados do usuário não encontrados');
        }

        try {
          const planRes = await api.get('/api/plans/my-plan');
          setTenantPlan(planRes.data);
        } catch (err) {
          console.warn('Erro ao carregar plano:', err);
        }
        
        if (userData.tenant_id && userData.tenant_status) {
          if (userData.tenant_status === 'suspended') {
            setError('TENANT_SUSPENDED');
            setLoading(false);
            return;
          }
          if (userData.tenant_status === 'cancelled') {
            setError('TENANT_CANCELLED');
            setLoading(false);
            return;
          }
        }

        setUser(userData);
      } catch (err) {
        console.error('Erro ao carregar dados do usuário:', err);
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          setError('TOKEN_EXPIRED');
        } else {
          setError('LOAD_ERROR');
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  if (error === 'NO_TOKEN' || error === 'TOKEN_EXPIRED') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (error === 'TENANT_SUSPENDED' || error === 'TENANT_CANCELLED' || error === 'LOAD_ERROR' || !user) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acesso Restrito</h2>
          <p className="text-gray-600 mb-4">{error === 'LOAD_ERROR' ? 'Erro ao carregar dados.' : 'Sua conta possui restrições.'}</p>
          <button onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }} className="px-6 py-2 bg-green-600 text-white rounded-xl font-medium">Sair</button>
        </div>
      </div>
    );
  }

  const baseRoute = location.pathname.match(/\/\d+$/) ? location.pathname.replace(/\/\d+$/, '') : location.pathname;
  const allowedRoles = routePermissions[baseRoute];

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={defaultRoutesByRole[user.role] || '/'} replace />;
  }

  return children;
};

export default ProtectedRoute;
